import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { chooseDefaultPaymentMethod, getStaffPaymentMethods } from "../api/paymentMethods";
import { topupWallet } from "../api/wallet";
import Button from "./common/Button";
import { formatCurrency } from "../utils/currency";
import { useRegionalPaymentDefaults } from "../context/RegionalSettingsContext";
import { TextInput, SelectInput } from "./common/form";

export default function WalletTopup({ studentId, onDone }) {
    const { t } = useTranslation();
    const { defaultPaymentMethod } = useRegionalPaymentDefaults();
    const [amount, setAmount] = useState("");
    const [mode, setMode] = useState(defaultPaymentMethod);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [ref, setRef] = useState("");
    const [loading, setLoading] = useState(false);
    const [methodsLoadError, setMethodsLoadError] = useState(false);

    React.useEffect(() => {
        let active = true;
        getStaffPaymentMethods()
            .then((rows) => {
                if (!active) return;
                setPaymentMethods(Array.isArray(rows) ? rows : []);
                setMode((prev) => chooseDefaultPaymentMethod(rows, defaultPaymentMethod || prev || "CASH"));
                setMethodsLoadError(false);
            })
            .catch((err) => {
                console.error("Failed to load payment methods", err);
                if (active) setMethodsLoadError(true);
            });
        return () => {
            active = false;
        };
    }, [defaultPaymentMethod]);

    const doTopup = async () => {
        if (!studentId) { alert(t('walletStaff.missingStudent')); return; }
        const a = Number(amount);
        if (!a || a <= 0) { alert(t('walletStaff.invalidAmount')); return; }
        setLoading(true);
        try {
            const payload = { studentId: Number(studentId), amount: a, paymentMode: mode, reference: ref, createdBy: null };
            await topupWallet(payload);
            alert(t('walletStaff.credited', { amount: formatCurrency(a) }));
            if (onDone) onDone();
        } catch (e) {
            console.error("topup failed", e);
            alert(apiErrorMessage(e, t('walletStaff.topupFailed')));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3 border rounded space-y-2 bg-white">
            <div className="text-sm font-semibold">{t('walletStaff.topupTitle')}</div>
            <TextInput
                type="number"
                placeholder={formatCurrency(0)}
                value={amount}
                onChange={e => setAmount(e.target.value)}
            />
            {methodsLoadError && (
                <p className="text-xs text-rose-700" role="status">
                    {t('walletStaff.paymentMethodsLoadFailed', { defaultValue: 'Could not load payment methods — using a fallback option.' })}
                </p>
            )}
            <SelectInput
                value={mode}
                onChange={e => setMode(e.target.value)}
            >
                {(paymentMethods.length ? paymentMethods : [{
                    methodKey: mode || 'CASH',
                    displayName: t('pages.wallet.methodCash', { defaultValue: 'Cash' }),
                }]).map((method) => (
                    <option key={method.methodKey} value={method.methodKey}>{method.displayName || method.methodKey}</option>
                ))}
            </SelectInput>
            <TextInput
                placeholder={t('walletStaff.referenceOptional')}
                value={ref}
                onChange={e => setRef(e.target.value)}
            />
            <div className="flex gap-2">
                <Button variant="secondary" onClick={() => { setAmount(""); setRef(""); }}>{t('walletStaff.clear')}</Button>
                <Button variant="primary" onClick={doTopup} disabled={loading}>{loading ? t('walletStaff.saving') : t('walletStaff.topup')}</Button>
            </div>
        </div>
    );
}
